import { mixins } from "vue-class-component";
import { Component, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BCol, BForm, BRow, BSpinner } from "@/lib/typed-bootstrap";

import ErrorHandling from "@/mixins/ErrorHandling.mixin";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { OrgModule } from "@/store/modules/org";
import FormTextarea from "../forms/inputs/FormTextarea.component";
import UpdateOrg from "./UpdateOrg.mixin";

import styles from "./DataProcessingAgreementForm.component.module.scss";

@Component
export default class DataProcessingAgreementForm extends mixins(
  UpdateOrg,
  UserPermissions,
  ErrorHandling,
) {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  $refs!: {
    form: HTMLFormElement;
  };

  dataCategories: string = "";
  subjectCategories: string = "";
  isLoading = false;

  get dataProcessingCategories() {
    return OrgModule.dataProcessingCategories
      ? { ...OrgModule.dataProcessingCategories }
      : { dataCategories: "", subjectCategories: "" };
  }

  @Watch("dataProcessingCategories", { immediate: true })
  onDataProcessingCategories() {
    this.reset();
  }

  get showAuthCancelSaveButtons(): boolean {
    if (!this.canManageOrg) return false;
    return (
      this.dataCategories !== this.dataProcessingCategories.dataCategories ||
      this.subjectCategories !== this.dataProcessingCategories.subjectCategories
    );
  }

  reset() {
    this.dataCategories = this.dataProcessingCategories.dataCategories || "";
    this.subjectCategories =
      this.dataProcessingCategories.subjectCategories || "";
  }

  async onSave() {
    try {
      this.isLoading = true;
      await this.updateOrg(() => ({
        id: OrgModule.id,
        dataProcessingCategories: JSON.stringify({
          dataCategories: this.dataCategories,
          subjectCategories: this.subjectCategories,
        }),
      }));
    } catch (err) {
      this.errorMessage = (err as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return (
      <div>
        <BAlert
          class="default__section"
          show={this.showErrorMessage}
          variant="danger"
          data-testid="data-processing-agreements-error-alert"
        >
          {this.errorMessage}
        </BAlert>

        <BRow class="default__section">
          <div class="default__section-header">
            {this.$t("org_settings_data_processing_title")}
          </div>
          <div class="default__section-subheader">
            {this.$t("org_settings_data_processing_subtitle")}
          </div>
        </BRow>

        <BRow class="default__section">
          <BForm
            ref="form"
            class="default__inline-form"
            acceptCharset="UTF-8"
            role="form"
            novalidate
            data-testid="dataProcessingAgreement"
          >
            <BRow cols="1" colsMd="2" class={styles.inputs}>
              <BCol>
                <FormTextarea
                  noResize
                  readonly={!this.canManageOrg}
                  rowsCount={4}
                  label={this.$t(
                    "org_settings_data_processing_categories_personalized_data",
                  ).toString()}
                  upperCaseLabel={true}
                  placeholder={this.$t(
                    "org_settings_data_processing_categories_data_placeholder",
                  ).toString()}
                  value={this.dataCategories}
                  onInput={(val: string) => (this.dataCategories = val)}
                />
              </BCol>

              <BCol class="mt-4 mt-sm-0">
                <FormTextarea
                  noResize
                  readonly={!this.canManageOrg}
                  rowsCount={4}
                  label={this.$t(
                    "org_settings_data_processing_categories_data_subjects",
                  ).toString()}
                  upperCaseLabel={true}
                  placeholder={this.$t(
                    "org_settings_data_processing_categories_subjects_placeholder",
                  ).toString()}
                  value={this.subjectCategories}
                  onInput={(val: string) => (this.subjectCategories = val)}
                />
              </BCol>
            </BRow>

            {this.showAuthCancelSaveButtons && (
              <BRow class="buttons__section">
                <button
                  type="reset"
                  class="action-button__pill--outline action-button__flex-props"
                  onClick={(e) => {
                    e.preventDefault();
                    this.reset();
                  }}
                  data-testid="dataProcessingAgreementCancelButton"
                >
                  {this.$t("global_cta_cancel")}
                </button>

                <button
                  type="submit"
                  class="action-button__pill--gradient action-button__flex-props"
                  onClick={(e) => {
                    e.preventDefault();
                    this.onSave();
                  }}
                  data-testid="dataProcessingAgreementSaveButton"
                >
                  {this.isLoading ? (
                    <div class="default__spinner--small" role="status">
                      <BSpinner
                        class="ml-auto"
                        type="grow"
                        label="Saving..."
                        small
                      />
                    </div>
                  ) : (
                    this.$t("global_cta_save")
                  )}
                </button>
              </BRow>
            )}
          </BForm>
        </BRow>
      </div>
    );
  }
}
